import React, { useEffect, useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { Check, Truck, ArrowRight, Clock, AlertCircle, Star } from 'lucide-react';
import { useAnalytics } from '../hooks/useAnalytics';
type Bundle = {
  id: string;
  bottles: number;
  title: string;
  subtitle: string;
  description: string;
  usageNote: string;
  price: number;
  originalPrice: number;
  savings: number;
  badge?: string;
  popular?: boolean;
  freeShipping: boolean;
};
const bundles: Bundle[] = [{
  id: '5-bottle',
  bottles: 5,
  title: 'Buy 3 Get 2 Free',
  subtitle: '5x GINEXT Ginger Oil',
  description: 'Built for daily use & long-term relief',
  usageNote: "Ideal if you're using GINEXT on multiple areas or want uninterrupted relief.",
  price: 89.97,
  originalPrice: 149.95,
  savings: 60,
  badge: 'BEST VALUE',
  popular: true,
  freeShipping: true
}, {
  id: '3-bottle',
  bottles: 3,
  title: 'Buy 2 Get 1 Free',
  subtitle: '3x GINEXT Ginger Oil',
  description: 'Enough to feel real change — without running out',
  usageNote: 'Covers 30–60 days of regular use for most people',
  price: 59.98,
  originalPrice: 89.97,
  savings: 33,
  badge: 'MOST POPULAR',
  freeShipping: true
}, {
  id: '1-bottle',
  bottles: 1,
  title: 'Single Bottle',
  subtitle: '1x GINEXT Ginger Oil',
  description: 'Best for first-time testing',
  usageNote: 'One area • Short-term use',
  price: 29.99,
  originalPrice: 29.99,
  savings: 0,
  freeShipping: false
}];
function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  useEffect(() => {
    // Get or set the end time in localStorage (36 hours from first visit)
    const getEndTime = () => {
      const stored = localStorage.getItem('ginext_offer_end_time');
      if (stored) {
        return parseInt(stored, 10);
      } else {
        const now = new Date().getTime();
        const endTime = now + 36 * 60 * 60 * 1000; // 36 hours from now
        localStorage.setItem('ginext_offer_end_time', endTime.toString());
        return endTime;
      }
    };
    const endTime = getEndTime();
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const difference = endTime - now;
      if (difference > 0) {
        setTimeLeft({
          hours: Math.floor(difference / (1000 * 60 * 60) % 100),
          minutes: Math.floor(difference / 1000 / 60 % 60),
          seconds: Math.floor(difference / 1000 % 60)
        });
      } else {
        // Timer expired, reset for another 36 hours
        const newEndTime = new Date().getTime() + 36 * 60 * 60 * 1000;
        localStorage.setItem('ginext_offer_end_time', newEndTime.toString());
        setTimeLeft({
          hours: 36,
          minutes: 0,
          seconds: 0
        });
      }
    };
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);
  return <div className="flex items-center gap-1.5" data-id="element-321">
      <div className="bg-charcoal text-white px-2 py-1 rounded font-mono text-xs font-medium min-w-[2rem] text-center" data-id="element-322">
        {String(timeLeft.hours).padStart(2, '0')}
      </div>
      <span className="text-charcoal text-xs font-medium" data-id="element-323">:</span>
      <div className="bg-charcoal text-white px-2 py-1 rounded font-mono text-xs font-medium min-w-[2rem] text-center" data-id="element-324">
        {String(timeLeft.minutes).padStart(2, '0')}
      </div>
      <span className="text-charcoal text-xs font-medium" data-id="element-325">:</span>
      <div className="bg-charcoal text-white px-2 py-1 rounded font-mono text-xs font-medium min-w-[2rem] text-center" data-id="element-326">
        {String(timeLeft.seconds).padStart(2, '0')}
      </div>
    </div>;
}
export function BundleSection() {
  const [selectedBundle, setSelectedBundle] = useState<Bundle>(bundles[0]);
  const {
    trackConversion,
    trackClick
  } = useAnalytics();
  const handlePurchase = (bundle: Bundle) => {
    // Track click in our analytics
    trackClick({
      location: 'bundle_section',
      bundleId: bundle.id,
      value: bundle.price,
      bottles: bundle.bottles
    });
    // Track conversion in our analytics
    trackConversion({
      bundleId: bundle.id,
      value: bundle.price,
      bottles: bundle.bottles
    });
    // Fire Meta Pixel AddToCart event
    if (window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: bundle.subtitle,
        content_ids: [bundle.id === '5-bottle' ? '45467602845882' : bundle.id === '3-bottle' ? '45467602124986' : '45467601207482'],
        content_type: 'product',
        value: bundle.price,
        currency: 'USD',
        num_items: bundle.bottles
      });
    }
  };
  return <section id="bundles" className="py-10 sm:py-12 md:py-16 bg-white scroll-mt-20" data-id="element-327">
      <div className="max-w-6xl mx-auto px-4" data-id="element-328">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center mb-6 sm:mb-8" data-id="element-329">
          <h2 className="text-ginger/60 font-light tracking-[0.2em] uppercase mb-2 sm:mb-3 text-xs" data-id="element-330">
            Choose Your Bundle
          </h2>
          <h3 className="text-3xl sm:text-4xl md:text-5xl font-light text-charcoal mb-3 sm:mb-4 tracking-tight" data-id="element-331">
            Relief Works Better When It's Consistent
          </h3>

          {/* Product Image */}
          <div className="max-w-md mx-auto mb-4 sm:mb-5" data-id="element-332">
            <img src="https://cdn.magicpatterns.com/uploads/wNaxZNT87oMaxD7uzqvyDW/ginext_banner.png" alt="GINEXT Ginger Oil" className="w-full h-auto rounded-xl shadow-lg lg:hidden" loading="lazy" decoding="async" width="800" height="400" data-id="element-333" />
          </div>

          <p className="text-sm sm:text-base text-charcoal-light max-w-2xl mx-auto font-light mb-6" data-id="element-334">
            Most people use GINEXT daily. Choose a bundle that supports real
            recovery — not just a one-time application.
          </p>

          {/* Special Offer Banner with Countdown */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="mb-4" data-id="element-335">
            <div className="relative bg-white/60 backdrop-blur-sm border border-ginger/15 rounded-xl p-3 sm:p-4 shadow-sm max-w-3xl mx-auto" data-id="element-336">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3" data-id="element-337">
                <div className="text-center sm:text-left" data-id="element-338">
                  <div className="inline-block px-2.5 py-0.5 bg-ginger/10 rounded-full mb-1.5" data-id="element-339">
                    <span className="text-[10px] font-medium text-ginger uppercase tracking-wider" data-id="element-340">
                      Limited Time Offer
                    </span>
                  </div>
                  <h4 className="text-lg sm:text-xl font-light text-charcoal tracking-tight" data-id="element-341">
                    Save Up to{' '}
                    <span className="text-ginger font-normal" data-id="element-342">60% Off</span>
                  </h4>
                  <p className="text-xs sm:text-sm text-charcoal-light font-light" data-id="element-343">
                    Special pricing on 3 & 5 bottle bundles
                  </p>
                </div>
                <div className="flex flex-col items-center gap-1.5 bg-bone/50 rounded-lg px-3 sm:px-4 py-2 sm:py-2.5" data-id="element-344">
                  <div className="flex items-center gap-1.5 text-charcoal-light" data-id="element-345">
                    <Clock className="w-3 h-3" data-id="element-346" />
                    <p className="text-[10px] font-medium uppercase tracking-wide" data-id="element-347">
                      Offer Ends In
                    </p>
                  </div>
                  <CountdownTimer data-id="element-348" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Inventory Alert */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} data-id="element-349">
            <div className="bg-amber-50/80 backdrop-blur-sm border border-amber-200/60 rounded-lg p-2.5 sm:p-3 flex items-start gap-2 max-w-3xl mx-auto" data-id="element-350">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-351" />
              <div className="text-left" data-id="element-352">
                <p className="text-xs sm:text-sm text-amber-900 font-medium" data-id="element-353">
                  Low Stock Alert
                </p>
                <p className="text-[10px] sm:text-xs text-amber-800 font-light" data-id="element-354">
                  Due to high demand, inventory is running low. Secure your
                  bundle now to avoid missing out.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Bundle Cards */}
        <div className="space-y-2 mb-2 max-w-4xl lg:max-w-5xl mx-auto" data-id="element-355">
          {bundles.map((bundle, index) => <motion.div key={bundle.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          delay: index * 0.03
        }} onClick={() => setSelectedBundle(bundle)} className={`relative bg-white rounded-lg p-3 sm:p-4 lg:p-6 border cursor-pointer transition-all ${selectedBundle.id === bundle.id ? 'border-gray-300 shadow-lg' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-356">
              {/* Mobile/Tablet Layout - Enhanced Design */}
              <div className="lg:hidden" data-id="element-357">
                {/* Header with Title and Badge */}
                <div className="bg-[#2C3E50] text-white px-3 py-1.5 -mx-3 -mt-3 rounded-t-md mb-2 flex items-center justify-between" data-id="element-358">
                  <div className="flex items-center gap-2" data-id="element-359">
                    <h3 className="font-semibold text-xs" data-id="element-360">{bundle.title}</h3>
                    {bundle.savings > 0 && <span className="inline-block bg-[#4CAF50] text-white text-[9px] font-semibold px-1.5 py-0.5 rounded" data-id="element-361">
                        Save {bundle.savings}%
                      </span>}
                  </div>
                  {bundle.freeShipping && <div className="bg-[#1E5A8E] text-white text-[9px] font-medium px-1.5 py-0.5 rounded flex items-center gap-0.5" data-id="element-362">
                      <Truck className="w-2.5 h-2.5" data-id="element-363" />
                      FREE SHIPPING
                    </div>}
                </div>

                {/* Main Content Area */}
                <div className={`${selectedBundle.id === bundle.id ? 'bg-[#FFF9E6]' : 'bg-white'} -mx-3 px-3 pb-2`} data-id="element-364">
                  <div className="flex gap-2 mb-2" data-id="element-365">
                    {/* Product Image - Fixed Height */}
                    <div className="w-24 h-24 bg-bone rounded-md flex-shrink-0 overflow-hidden" data-id="element-366">
                      <img src={bundle.id === '5-bottle' ? 'https://cdn.magicpatterns.com/uploads/ikigb4DaYP29E1GRJ6x2Y6/Generated_Image_December_22,_2025_-_7_10PM.png' : bundle.id === '3-bottle' ? 'https://cdn.magicpatterns.com/uploads/nB1NbtSHBQy6Wka4AzbbBt/Generated_Image_December_22,_2025_-_4_26PM.png' : 'https://cdn.magicpatterns.com/uploads/sCzJPCpJhc9zMT9yg88sL6/Generated_Image_December_22,_2025_-_7_24PM.png'} alt="GINEXT Ginger Oil" className="w-full h-full object-cover" loading="lazy" decoding="async" width="200" height="200" data-id="element-367" />
                    </div>

                    {/* Product Info - Match Image Height */}
                    <div className="flex-1 min-w-0 flex flex-col h-24" data-id="element-368">
                      <h4 className="font-semibold text-[#1E5A8E] text-sm leading-tight" data-id="element-369">
                        {bundle.subtitle.replace('GINEXT ', '')}
                      </h4>
                      <p className="text-[10px] text-charcoal leading-tight" data-id="element-370">
                        {bundle.subtitle}
                      </p>
                      <p className="text-[10px] text-charcoal-light leading-tight" data-id="element-371">
                        Same as
                      </p>
                      <div className="flex items-baseline gap-1" data-id="element-372">
                        <span className="text-xl font-light text-charcoal leading-none tracking-tight" style={{
                      fontFamily: 'Georgia, serif'
                    }} data-id="element-373">
                          ${(bundle.price / bundle.bottles).toFixed(2)}
                        </span>
                        <span className="text-[10px] text-charcoal-light" data-id="element-374">
                          /each
                        </span>
                      </div>
                      {bundle.savings > 0 && <p className="text-[10px] text-charcoal-light line-through leading-tight" data-id="element-375">
                          Orig $
                          {(bundle.originalPrice / bundle.bottles).toFixed(2)}
                          /each
                        </p>}
                      <p className="text-xs font-semibold text-charcoal leading-tight mt-auto" style={{
                    fontFamily: 'Georgia, serif'
                  }} data-id="element-376">
                        Total: ${bundle.price.toFixed(2)}
                      </p>
                    </div>

                    {/* Radio Button */}
                    <div className="flex-shrink-0" data-id="element-377">
                      <div className={`w-5 h-5 rounded-sm border-2 flex items-center justify-center transition-all ${selectedBundle.id === bundle.id ? 'border-[#1E5A8E] bg-[#1E5A8E]' : 'border-charcoal-light'}`} data-id="element-378">
                        {selectedBundle.id === bundle.id && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-379" />}
                      </div>
                    </div>
                  </div>

                  {/* Feature Box - Blue Background - Only show when selected */}
                  {selectedBundle.id === bundle.id && <div className="bg-[#4A7BA7] text-white rounded-md p-2 mb-1" data-id="element-380">
                      <div className="grid grid-cols-2 gap-2 text-[10px] leading-tight" data-id="element-381">
                        <div data-id="element-382">
                          <p className="font-medium mb-0.5" data-id="element-383">
                            {bundle.bottles}x GINEXT Ginger Oil
                          </p>
                          <p className="opacity-90" data-id="element-384">Premium Grade</p>
                          <p className="opacity-90" data-id="element-385">Natural Formula</p>
                          <p className="opacity-90" data-id="element-386">Fast Acting</p>
                        </div>
                        <div data-id="element-387">
                          <p className="font-medium mb-0.5" data-id="element-388">
                            100% Natural Ginger
                          </p>
                          <p className="opacity-90" data-id="element-389">Warming Relief</p>
                          <p className="opacity-90" data-id="element-390">Anti-Inflammatory</p>
                          <p className="opacity-90" data-id="element-391">Circulation Support</p>
                        </div>
                      </div>
                    </div>}

                  {/* Bottom Info Row - Very Compact */}
                  <div className="flex items-center justify-between text-[8px] leading-none" data-id="element-392">
                    <div className="flex items-center gap-0.5 flex-wrap" data-id="element-393">
                      <span className="text-red-600 font-medium" data-id="element-394">
                        Sell-Out Risk: High
                      </span>
                      <span className="text-charcoal-light" data-id="element-395">•</span>
                      {bundle.freeShipping && <>
                          <span className="text-[#4CAF50] font-medium" data-id="element-396">
                            FREE SHIPPING
                          </span>
                          <span className="text-charcoal-light" data-id="element-397">•</span>
                        </>}
                      <span className="text-charcoal-light" data-id="element-398">
                        Discount: {bundle.savings}%
                      </span>
                    </div>
                    {bundle.badge && <span className="bg-charcoal text-white px-1 py-0.5 rounded text-[8px] font-medium flex items-center gap-0.5 leading-none" data-id="element-399">
                        <Check className="w-2 h-2" data-id="element-400" />
                        {bundle.badge === 'BEST VALUE' ? 'Best Seller' : bundle.badge === 'MOST POPULAR' ? 'Popular' : 'Moderate'}
                      </span>}
                  </div>
                </div>
              </div>

              {/* Desktop Layout - Completely Redesigned Compact Version */}
              <div className="hidden lg:block" data-id="element-401">
                {/* Compact Header - Title and Badges on Same Line */}
                <div className="bg-[#2C3E50] text-white px-4 py-2 -mx-6 -mt-6 rounded-t-lg mb-0 flex items-center justify-between" data-id="element-402">
                  <div className="flex items-center gap-3" data-id="element-403">
                    <h3 className="font-semibold text-base" data-id="element-404">{bundle.title}</h3>
                    {bundle.savings > 0 && <span className="inline-block bg-[#4CAF50] text-white text-xs font-semibold px-2 py-0.5 rounded" data-id="element-405">
                        Save {bundle.savings}%
                      </span>}
                  </div>
                  {bundle.freeShipping && <div className="bg-[#1E5A8E] text-white text-xs font-medium px-3 py-1 rounded flex items-center gap-1.5" data-id="element-406">
                      <Truck className="w-3.5 h-3.5" data-id="element-407" />
                      FREE SHIPPING
                    </div>}
                </div>

                {/* Main Content Area - Horizontal Compact Layout */}
                <div className={`${selectedBundle.id === bundle.id ? 'bg-[#FFF9E6]' : 'bg-white'} -mx-6 px-4 py-3`} data-id="element-408">
                  {/* Horizontal Layout: Image | Product Info | Checkbox */}
                  <div className="flex items-start gap-4 mb-3" data-id="element-409">
                    {/* Product Image - Smaller */}
                    <div className="w-40 h-40 bg-bone rounded-md flex-shrink-0 overflow-hidden" data-id="element-410">
                      <img src={bundle.id === '5-bottle' ? 'https://cdn.magicpatterns.com/uploads/ikigb4DaYP29E1GRJ6x2Y6/Generated_Image_December_22,_2025_-_7_10PM.png' : bundle.id === '3-bottle' ? 'https://cdn.magicpatterns.com/uploads/nB1NbtSHBQy6Wka4AzbbBt/Generated_Image_December_22,_2025_-_4_26PM.png' : 'https://cdn.magicpatterns.com/uploads/sCzJPCpJhc9zMT9yg88sL6/Generated_Image_December_22,_2025_-_7_24PM.png'} alt="GINEXT Ginger Oil" className="w-full h-full object-cover" loading="lazy" decoding="async" width="400" height="400" data-id="element-411" />
                    </div>

                    {/* Product Info - Compact */}
                    <div className="flex-1 min-w-0" data-id="element-412">
                      <h4 className="font-semibold text-[#1E5A8E] text-xl mb-1 leading-tight" data-id="element-413">
                        {bundle.subtitle.replace('GINEXT ', '')}
                      </h4>
                      <p className="text-sm text-charcoal mb-2 leading-tight" data-id="element-414">
                        {bundle.subtitle}
                      </p>
                      <p className="text-sm text-charcoal-light mb-1 leading-tight" data-id="element-415">
                        Same as
                      </p>
                      <div className="mb-1" data-id="element-416">
                        <span className="text-3xl font-light text-charcoal tracking-tight leading-none" style={{
                      fontFamily: 'Georgia, serif'
                    }} data-id="element-417">
                          ${(bundle.price / bundle.bottles).toFixed(2)}
                        </span>
                        <span className="text-sm text-charcoal-light" data-id="element-418">
                          /each
                        </span>
                      </div>
                      {bundle.savings > 0 && <p className="text-sm text-charcoal-light line-through mb-1 leading-tight" data-id="element-419">
                          Orig $
                          {(bundle.originalPrice / bundle.bottles).toFixed(2)}
                          /each
                        </p>}
                      <p className="text-lg font-semibold text-charcoal leading-tight" style={{
                    fontFamily: 'Georgia, serif'
                  }} data-id="element-420">
                        Total: ${bundle.price.toFixed(2)}
                      </p>
                    </div>

                    {/* Checkbox - Right Side */}
                    <div className="flex-shrink-0" data-id="element-421">
                      <div className={`w-8 h-8 rounded-sm border-2 flex items-center justify-center transition-all ${selectedBundle.id === bundle.id ? 'border-[#1E5A8E] bg-[#1E5A8E]' : 'border-charcoal-light'}`} data-id="element-422">
                        {selectedBundle.id === bundle.id && <Check className="w-5 h-5 text-white" strokeWidth={3} data-id="element-423" />}
                      </div>
                    </div>
                  </div>

                  {/* Feature Box - Full Width Below */}
                  {bundle.bottles > 1 && <div className="bg-[#4A7BA7] text-white rounded-md p-3 mb-2" data-id="element-424">
                      <div className="grid grid-cols-2 gap-4 text-sm leading-tight" data-id="element-425">
                        <div data-id="element-426">
                          <p className="font-medium mb-1" data-id="element-427">
                            {bundle.bottles}x GINEXT Ginger Oil
                          </p>
                          <p className="opacity-90" data-id="element-428">Premium Grade</p>
                          <p className="opacity-90" data-id="element-429">Natural Formula</p>
                          <p className="opacity-90" data-id="element-430">Fast Acting</p>
                        </div>
                        <div data-id="element-431">
                          <p className="font-medium mb-1" data-id="element-432">
                            100% Natural Ginger
                          </p>
                          <p className="opacity-90" data-id="element-433">Warming Relief</p>
                          <p className="opacity-90" data-id="element-434">Anti-Inflammatory</p>
                          <p className="opacity-90" data-id="element-435">Circulation Support</p>
                        </div>
                      </div>
                    </div>}

                  {/* Bottom Info Row - Compact */}
                  <div className="flex items-center justify-between text-sm" data-id="element-436">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-437">
                      <span className="text-red-600 font-medium" data-id="element-438">
                        Sell-Out Risk: High
                      </span>
                      <span className="text-charcoal-light" data-id="element-439">•</span>
                      {bundle.freeShipping && <>
                          <span className="text-[#4CAF50] font-medium" data-id="element-440">
                            FREE SHIPPING
                          </span>
                          <span className="text-charcoal-light" data-id="element-441">•</span>
                        </>}
                      <span className="text-charcoal-light" data-id="element-442">
                        Discount: {bundle.savings}%
                      </span>
                    </div>
                    {bundle.badge && <span className="bg-charcoal text-white px-2.5 py-1 rounded text-sm font-medium flex items-center gap-1" data-id="element-443">
                        <Check className="w-3.5 h-3.5" data-id="element-444" />
                        {bundle.badge === 'BEST VALUE' ? 'Best Seller' : bundle.badge === 'MOST POPULAR' ? 'Popular' : 'Moderate'}
                      </span>}
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* CTA Button - Moved here, right after bundle cards */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.35
      }} className="text-center max-w-4xl lg:max-w-5xl mx-auto mb-4 sm:mb-6" data-id="element-445">
          {selectedBundle.id === '5-bottle' ? <a href="https://www.ginextwarming.com/cart/45467602845882:1" className="inline-block w-full sm:w-auto lg:w-full" onClick={() => handlePurchase(selectedBundle)} data-id="element-446">
              <motion.div whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="w-full sm:w-auto lg:w-full group bg-ginger text-white px-6 sm:px-8 py-4 sm:py-5 lg:py-6 rounded-lg font-bold text-base sm:text-lg lg:text-xl shadow-warm hover:shadow-warm-hover transition-all duration-300 mx-auto" data-id="element-447">
                <div className="flex items-center justify-center gap-2 mb-1" data-id="element-448">
                  <span data-id="element-449">
                    Get {selectedBundle.bottles} Bottles for $
                    {selectedBundle.price.toFixed(2)}
                  </span>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" data-id="element-450" />
                </div>
                <p className="text-xs sm:text-sm lg:text-base font-normal text-white/90 text-center" data-id="element-451">
                  30 Days Money Back Guaranteed
                </p>
              </motion.div>
            </a> : selectedBundle.id === '3-bottle' ? <a href="https://www.ginextwarming.com/cart/45467602124986:1" className="inline-block w-full sm:w-auto lg:w-full" onClick={() => handlePurchase(selectedBundle)} data-id="element-452">
              <motion.div whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="w-full sm:w-auto lg:w-full group bg-ginger text-white px-6 sm:px-8 py-4 sm:py-5 lg:py-6 rounded-lg font-bold text-base sm:text-lg lg:text-xl shadow-warm hover:shadow-warm-hover transition-all duration-300 mx-auto" data-id="element-453">
                <div className="flex items-center justify-center gap-2 mb-1" data-id="element-454">
                  <span data-id="element-455">
                    Get {selectedBundle.bottles} Bottles for $
                    {selectedBundle.price.toFixed(2)}
                  </span>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" data-id="element-456" />
                </div>
                <p className="text-xs sm:text-sm lg:text-base font-normal text-white/90 text-center" data-id="element-457">
                  30 Days Money Back Guaranteed
                </p>
              </motion.div>
            </a> : <a href="https://www.ginextwarming.com/cart/45467601207482:1" className="inline-block w-full sm:w-auto lg:w-full" onClick={() => handlePurchase(selectedBundle)} data-id="element-458">
              <motion.div whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="w-full sm:w-auto lg:w-full group bg-ginger text-white px-6 sm:px-8 py-4 sm:py-5 lg:py-6 rounded-lg font-bold text-base sm:text-lg lg:text-xl shadow-warm hover:shadow-warm-hover transition-all duration-300 mx-auto" data-id="element-459">
                <div className="flex items-center justify-center gap-2 mb-1" data-id="element-460">
                  <span data-id="element-461">
                    Get {selectedBundle.bottles} Bottle
                    {selectedBundle.bottles > 1 ? 's' : ''} for $
                    {selectedBundle.price.toFixed(2)}
                  </span>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" data-id="element-462" />
                </div>
                <p className="text-xs sm:text-sm lg:text-base font-normal text-white/90 text-center" data-id="element-463">
                  30 Days Money Back Guaranteed
                </p>
              </motion.div>
            </a>}
          {/* Trust Stats - Below CTA */}
          <div className="mt-3 sm:mt-4 flex flex-col items-center gap-1" data-id="element-464">
            <div className="flex items-center gap-[2px]" aria-label="4.8 out of 5 stars" data-id="element-465">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-ginger text-ginger" data-id="element-466" />)}
            </div>
            <p className="text-xs sm:text-sm text-charcoal-light" data-id="element-467">
              <strong className="font-semibold text-charcoal" data-id="element-468">4.8 stars</strong>
              <span className="mx-1.5 text-charcoal-light/50" data-id="element-469">|</span>
              <strong className="font-semibold text-charcoal" data-id="element-470">10K+</strong>{' '}
              reviews
              <span className="mx-1.5 text-charcoal-light/50" data-id="element-471">|</span>
              <strong className="font-semibold text-charcoal" data-id="element-472">10K+</strong>{' '}
              members
            </p>
          </div>
          <p className="mt-2 text-xs sm:text-sm text-charcoal-light" data-id="element-473">
            {selectedBundle.freeShipping ? 'Free Shipping' : 'Shipping Calculated at Checkout'}{' '}
            • Secure Checkout
          </p>
        </motion.div>

        {/* AOV Nudge */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="text-center mb-4 sm:mb-6 max-w-3xl mx-auto" data-id="element-474">
          <p className="text-xs sm:text-sm text-charcoal-light italic font-light" data-id="element-475">
            Most customers choose a bundle to avoid reordering once relief
            begins.
          </p>
        </motion.div>

        {/* Risk Reversal */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.25
      }} className="text-center mb-4 sm:mb-6 max-w-3xl mx-auto bg-white rounded-lg p-3 border border-bone-dark" data-id="element-476">
          <p className="text-xs sm:text-sm text-charcoal font-light" data-id="element-477">
            The 30-day money-back guarantee applies to all bundles so there's no
            risk in choosing the option that lasts longer.
          </p>
        </motion.div>

        {/* Final Micro-Copy */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="text-center mb-4 sm:mb-6" data-id="element-478">
          <p className="text-xs sm:text-sm text-charcoal font-light" data-id="element-479">
            Choose the option that supports how you actually want to feel not
            just today, but going forward.
          </p>
        </motion.div>
      </div>
    </section>;
}